// AudioManager — Web Audio API engine for music + SFX
// Bus graph: AudioContext → masterGain → destination
//            musicGain → masterGain
//            sfxGain   → masterGain
//            uiGain    → masterGain

import { MUSIC_TRACKS, SFX_MAP, SFX_COOLDOWNS, SFX_MAX_POLYPHONY } from './audioManifest.mjs';

const LS_KEY = 'hexaworlds_audio';

const DEFAULT_VOLUMES = {
  master: 0.8,
  music: 0.5,
  sfx: 0.35,
  ui: 0.25,
  ambience: 0.30,
  muted: false,
};

export class AudioManager {
  constructor() {
    this.ctx = null;
    this.masterGain = null;
    this.musicGain = null;
    this.sfxGain = null;
    this.uiGain = null;
    this.ambienceGain = null;
    this.unlocked = false;

    // Volumes (restored from localStorage or defaults)
    this.volumes = this._loadVolumes();

    // Decoded buffers
    this.musicBuffers = new Map();   // trackId → AudioBuffer
    this.sfxBuffers = new Map();     // sfxId → AudioBuffer[]

    // Music state
    this.playlist = [];
    this.playlistIndex = 0;
    this.shuffle = true;
    this.crossfadeSec = 2.5;
    this.musicPlaying = false;
    this.currentMusic = null; // { source, gain, trackId, startedAt }
    this.nextMusicTimeout = null;

    // SFX spam control
    this._lastPlayMs = new Map();    // sfxId → timestamp
    this._activeSources = new Map(); // sfxId → Set<AudioBufferSourceNode>
  }

  // ─── Unlock ──────────────────────────────────────────────

  async unlock() {
    if (this.unlocked) return;
    try {
      this.ctx = new (window.AudioContext || window.webkitAudioContext)();
      if (this.ctx.state === 'suspended') {
        await this.ctx.resume();
      }

      // Build gain graph
      this.masterGain = this.ctx.createGain();
      this.masterGain.connect(this.ctx.destination);

      this.musicGain = this.ctx.createGain();
      this.musicGain.connect(this.masterGain);

      this.sfxGain = this.ctx.createGain();
      this.sfxGain.connect(this.masterGain);

      this.uiGain = this.ctx.createGain();
      this.uiGain.connect(this.masterGain);

      this.ambienceGain = this.ctx.createGain();
      this.ambienceGain.connect(this.masterGain);

      // Apply persisted volumes
      this._applyVolumes();

      this.unlocked = true;
      console.log('[Audio] Unlocked, AudioContext state:', this.ctx.state);
    } catch (e) {
      console.warn('[Audio] Failed to unlock:', e);
    }
  }

  isUnlocked() {
    return this.unlocked;
  }

  // ─── Loading ─────────────────────────────────────────────

  async _loadBuffer(url) {
    const resp = await fetch(url);
    const buf = await resp.arrayBuffer();
    return this.ctx.decodeAudioData(buf);
  }

  async preloadSfx() {
    if (!this.ctx) return;
    const jobs = [];
    for (const [id, variants] of Object.entries(SFX_MAP)) {
      const buffers = [];
      for (const variant of variants) {
        jobs.push(
          this._loadBuffer(variant.url)
            .then(buf => buffers.push(buf))
            .catch(e => console.warn(`[Audio] SFX load fail: ${id}`, e))
        );
      }
      this.sfxBuffers.set(id, buffers);
    }
    await Promise.all(jobs);
    console.log('[Audio] SFX preloaded:', this.sfxBuffers.size, 'ids');
  }

  async preloadMusic(trackIds) {
    if (!this.ctx) return;
    const tracks = trackIds
      ? MUSIC_TRACKS.filter(t => trackIds.includes(t.id))
      : MUSIC_TRACKS;
    for (const track of tracks) {
      if (this.musicBuffers.has(track.id)) continue;
      try {
        const buf = await this._loadBuffer(track.url);
        this.musicBuffers.set(track.id, buf);
      } catch (e) {
        console.warn(`[Audio] Music load fail: ${track.id}`, e);
      }
    }
    console.log('[Audio] Music preloaded:', this.musicBuffers.size, 'tracks');
  }

  // ─── Volume ──────────────────────────────────────────────

  setVolume(bus, value01) {
    this.volumes[bus] = Math.max(0, Math.min(1, value01));
    this._applyVolumes();
    this._saveVolumes();
  }

  getVolume(bus) {
    return this.volumes[bus];
  }

  setMuted(muted) {
    this.volumes.muted = !!muted;
    this._applyVolumes();
    this._saveVolumes();
  }

  isMuted() {
    return this.volumes.muted;
  }

  _applyVolumes() {
    if (!this.masterGain) return;
    const m = this.volumes.muted ? 0 : this.volumes.master;
    this.masterGain.gain.setValueAtTime(m, this.ctx.currentTime);
    this.musicGain.gain.setValueAtTime(this.volumes.music, this.ctx.currentTime);
    this.sfxGain.gain.setValueAtTime(this.volumes.sfx, this.ctx.currentTime);
    this.uiGain.gain.setValueAtTime(this.volumes.ui, this.ctx.currentTime);
    if (this.ambienceGain) {
      this.ambienceGain.gain.setValueAtTime(this.volumes.ambience, this.ctx.currentTime);
    }
  }

  _saveVolumes() {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(this.volumes));
    } catch (_) { /* quota */ }
  }

  _loadVolumes() {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const v = JSON.parse(raw);
        return { ...DEFAULT_VOLUMES, ...v };
      }
    } catch (_) { /* ignore */ }
    return { ...DEFAULT_VOLUMES };
  }

  // ─── Music playlist ──────────────────────────────────────

  setPlaylist(trackIds, opts = {}) {
    this.playlist = [...trackIds];
    if (opts.shuffle !== undefined) this.shuffle = opts.shuffle;
    if (opts.crossfadeSec !== undefined) this.crossfadeSec = opts.crossfadeSec;
    if (this.shuffle && this.playlist.length > 1) {
      // Fisher-Yates shuffle
      for (let i = this.playlist.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [this.playlist[i], this.playlist[j]] = [this.playlist[j], this.playlist[i]];
      }
    }
    this.playlistIndex = 0;
  }

  async playMusic() {
    if (!this.ctx || this.playlist.length === 0) return;
    this.musicPlaying = true;
    await this._playTrack(this.playlist[this.playlistIndex]);
  }

  pauseMusic() {
    this.musicPlaying = false;
    if (this.nextMusicTimeout) {
      clearTimeout(this.nextMusicTimeout);
      this.nextMusicTimeout = null;
    }
    if (this.currentMusic) {
      const { source, gain } = this.currentMusic;
      // Fade out quickly
      gain.gain.setValueAtTime(gain.gain.value, this.ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0, this.ctx.currentTime + 0.3);
      const s = source;
      setTimeout(() => { try { s.stop(); } catch (_) {} }, 350);
      this.currentMusic = null;
    }
  }

  async skipTrack() {
    if (!this.musicPlaying) return;
    this._advancePlaylist();
    // Crossfade from current
    if (this.currentMusic) {
      const old = this.currentMusic;
      old.gain.gain.setValueAtTime(old.gain.gain.value, this.ctx.currentTime);
      old.gain.gain.linearRampToValueAtTime(0, this.ctx.currentTime + this.crossfadeSec);
      setTimeout(() => { try { old.source.stop(); } catch (_) {} }, this.crossfadeSec * 1000 + 100);
    }
    if (this.nextMusicTimeout) {
      clearTimeout(this.nextMusicTimeout);
      this.nextMusicTimeout = null;
    }
    await this._playTrack(this.playlist[this.playlistIndex]);
  }

  async _playTrack(trackId) {
    if (!this.ctx) return;
    // Ensure loaded
    if (!this.musicBuffers.has(trackId)) {
      const track = MUSIC_TRACKS.find(t => t.id === trackId);
      if (!track) return;
      try {
        const buf = await this._loadBuffer(track.url);
        this.musicBuffers.set(trackId, buf);
      } catch (e) {
        console.warn('[Audio] Music load fail:', trackId, e);
        return;
      }
    }

    const buffer = this.musicBuffers.get(trackId);
    const source = this.ctx.createBufferSource();
    source.buffer = buffer;

    // For single-track playlist, loop it directly
    if (this.playlist.length === 1) {
      source.loop = true;
    }

    const trackGain = this.ctx.createGain();
    trackGain.connect(this.musicGain);
    source.connect(trackGain);

    // Fade in
    trackGain.gain.setValueAtTime(0, this.ctx.currentTime);
    trackGain.gain.linearRampToValueAtTime(1, this.ctx.currentTime + Math.min(this.crossfadeSec, 1));

    source.start(0);

    this.currentMusic = {
      source,
      gain: trackGain,
      trackId,
      startedAt: this.ctx.currentTime,
    };

    // Schedule next track (only for multi-track playlists)
    if (this.playlist.length > 1) {
      const dur = buffer.duration;
      const scheduleMs = Math.max(0, (dur - this.crossfadeSec)) * 1000;
      this.nextMusicTimeout = setTimeout(() => {
        if (!this.musicPlaying) return;
        // Fade out current
        if (this.currentMusic && this.currentMusic.trackId === trackId) {
          const old = this.currentMusic;
          old.gain.gain.setValueAtTime(old.gain.gain.value, this.ctx.currentTime);
          old.gain.gain.linearRampToValueAtTime(0, this.ctx.currentTime + this.crossfadeSec);
          setTimeout(() => { try { old.source.stop(); } catch (_) {} }, this.crossfadeSec * 1000 + 100);
        }
        this._advancePlaylist();
        this._playTrack(this.playlist[this.playlistIndex]);
      }, scheduleMs);
    }

    // Handle natural end for single-track non-loop case
    source.onended = () => {
      if (this.currentMusic && this.currentMusic.source === source) {
        this.currentMusic = null;
      }
    };

    // Preload next track in background for seamless transition
    this._preloadNext();
  }

  /** Preload the next track in the playlist so it's ready for crossfade. */
  _preloadNext() {
    if (this.playlist.length <= 1) return;
    const nextIndex = (this.playlistIndex + 1) % this.playlist.length;
    const nextId = this.playlist[nextIndex];
    if (this.musicBuffers.has(nextId)) return;
    const track = MUSIC_TRACKS.find(t => t.id === nextId);
    if (!track) return;
    this._loadBuffer(track.url)
      .then(buf => this.musicBuffers.set(nextId, buf))
      .catch(e => console.warn('[Audio] Next track preload fail:', nextId, e));
  }

  _advancePlaylist() {
    this.playlistIndex = (this.playlistIndex + 1) % this.playlist.length;
  }

  // ─── SFX ─────────────────────────────────────────────────

  /** Return the ambience bus for AmbienceManager to connect to */
  getAmbienceBus() {
    if (!this.ctx || !this.ambienceGain) return null;
    return { audioContext: this.ctx, ambienceGain: this.ambienceGain };
  }

  playSfx(id, opts = {}) {
    if (!this.ctx || !this.unlocked) return;

    const buffers = this.sfxBuffers.get(id);
    if (!buffers || buffers.length === 0) return;

    // Cooldown check
    const now = performance.now();
    const cooldown = SFX_COOLDOWNS[id] || 0;
    const lastPlay = this._lastPlayMs.get(id) || 0;
    if (now - lastPlay < cooldown) return;

    // Max polyphony check
    const maxPoly = SFX_MAX_POLYPHONY[id] || 8;
    let activeSources = this._activeSources.get(id);
    if (!activeSources) {
      activeSources = new Set();
      this._activeSources.set(id, activeSources);
    }
    if (activeSources.size >= maxPoly) return;

    this._lastPlayMs.set(id, now);

    // Pick random variant
    const buffer = buffers[Math.floor(Math.random() * buffers.length)];

    const source = this.ctx.createBufferSource();
    source.buffer = buffer;

    // Random pitch ±2%
    const rateMin = opts.rateMin || 0.98;
    const rateMax = opts.rateMax || 1.02;
    source.playbackRate.value = rateMin + Math.random() * (rateMax - rateMin);

    // Determine bus: ui_ prefixed sounds go to uiGain, others to sfxGain
    const bus = id.startsWith('ui_') ? this.uiGain : this.sfxGain;

    // Optional per-sound gain
    if (opts.gain !== undefined && opts.gain !== 1) {
      const g = this.ctx.createGain();
      g.gain.value = opts.gain;
      source.connect(g);
      g.connect(bus);
    } else {
      source.connect(bus);
    }

    activeSources.add(source);
    source.onended = () => {
      activeSources.delete(source);
    };

    source.start(0);
  }
}
